/**
 * AuditeeRequestList — the auditee's request-and-respond surface, shared by
 * the public portal (token auth) and the internal "My audits" view (session
 * auth). The caller passes a fetch adapter ({ respond, upload }), so this
 * component never knows which auth it runs under.
 *
 * GOVERNING CONSTRAINT: this is auditee-facing. Plain human language only.
 * No auditor vocabulary here, ever.
 */
import React, { useRef, useState } from 'react';

export const AUDITEE_KIND_LABELS = {
  documentation: 'Document request',
  question: 'Question',
  root_cause: 'Root cause analysis',
  impact: 'Impact analysis',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const fmtSize = (b) => (b > 0 ? `${Math.max(1, Math.round(b / 1024))} KB` : '');

function duePill(r) {
  if (!r.due_date) return null;
  const due = String(r.due_date).slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const answered = !!r.responded_at;
  const soon = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  if (!answered && due < today) return { text: `Overdue, was due ${due}`, cls: 'bg-red-50 text-red-700 border-red-200' };
  if (!answered && due <= soon) return { text: `Due ${due}`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { text: `Due ${due}`, cls: 'bg-slate-50 text-slate-500 border-slate-200' };
}

function FileDropZone({ onFile, busy }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f && !busy) onFile(f); }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`border border-dashed rounded-lg px-3 py-2.5 cursor-pointer transition-colors text-center ${over ? 'border-orange-400 bg-orange-50' : 'border-slate-300 bg-slate-50/60 hover:border-orange-300 hover:bg-orange-50/40'}`}
    >
      <input ref={inputRef} type="file" className="hidden" data-testid="auditee-upload-input" accept=".pdf,.docx,.xlsx,.csv,.txt" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} disabled={busy} />
      <div className="text-[12px] text-slate-600 font-medium">{busy ? 'Uploading…' : 'Drag a file here, or click to attach'}</div>
      <div className="text-[10px] text-slate-400">PDF, Word, Excel, CSV, or text, up to 25 MB</div>
      <div className="text-[10px] text-sky-700 mt-0.5" title="Files containing Social Security numbers, Medicare numbers, or other member identifiers are quarantined automatically and Compliance is alerted so the information stays protected. Reporting never gets anyone in trouble.">
        Please leave member details out (names, DOBs, SSNs, Medicare numbers), refer to cases by sample number.
        If PHI slips in, that is okay, just let Compliance know.
      </div>
    </div>
  );
}

function RequestCard({ r, uploads, adapter, onChanged }) {
  const [text, setText] = useState(r.response_text || '');
  const [editing, setEditing] = useState(!r.responded_at);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const pill = duePill(r);
  const mine = (uploads || []).filter((u) => u.request_id === r.id);

  const send = async () => {
    setBusy('respond'); setErr('');
    try { await adapter.respond(r.id, text); setEditing(false); await onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy('');
  };
  const attach = async (file) => {
    setBusy('upload'); setErr('');
    try { await adapter.upload(r.id, file); await onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy('');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wide bg-orange-50 text-orange-700 border border-orange-100 rounded px-1.5 py-0.5">
          {AUDITEE_KIND_LABELS[r.kind] || 'Request'}
        </span>
        {pill && <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${pill.cls}`}>{pill.text}</span>}
        {r.responded_at && <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Answered</span>}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-800">{r.title || AUDITEE_KIND_LABELS[r.kind] || 'Request'}</div>
        <div className="text-[10px] text-slate-400">Asked {fmtDate(r.requested_at)}</div>
      </div>

      {editing ? (
        <div className="space-y-1.5">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Write your answer here…" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 leading-relaxed" />
          <div className="flex items-center gap-2">
            <button type="button" onClick={send} disabled={busy === 'respond' || !text.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
              {busy === 'respond' ? 'Sending…' : 'Send answer'}
            </button>
            {r.responded_at && <button type="button" onClick={() => { setEditing(false); setText(r.response_text || ''); }} className="text-[11px] text-slate-400 hover:text-slate-600">Cancel</button>}
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Answer sent {fmtDate(r.responded_at)}</div>
          {r.response_text && <p className="text-[12px] text-slate-700 whitespace-pre-wrap">{r.response_text}</p>}
          <button type="button" onClick={() => setEditing(true)} className="text-[11px] font-semibold text-orange-600 hover:underline">Edit your answer</button>
        </div>
      )}

      <FileDropZone onFile={attach} busy={busy === 'upload'} />
      {mine.length > 0 && (
        <div className="space-y-0.5">
          {mine.map((u) => (
            <div key={u.id} className="text-[11px] text-slate-500 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
              <span className="text-slate-700 font-medium truncate">{u.file_name}</span>
              <span className="text-slate-400 flex-shrink-0">{fmtSize(u.file_size_bytes)} · {fmtDate(u.created_at)}</span>
            </div>
          ))}
        </div>
      )}
      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
    </div>
  );
}

export default function AuditeeRequestList({ requests, uploads, adapter, onChanged }) {
  if (!requests || !requests.length) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-10 text-center text-sm text-slate-500">
        Nothing is waiting on you right now.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {requests.map((r) => (
        <RequestCard key={r.id} r={r} uploads={uploads} adapter={adapter} onChanged={onChanged} />
      ))}
    </div>
  );
}
